<template>
	<transition name="scroll-to-top">
		<div v-if="isVisible" class="scroll-to-top-container">
			<!-- Прогресс-индикатор -->
			<svg
				class="progress-ring"
				:width="ringSize"
				:height="ringSize"
				:viewBox="`0 0 ${ringSize} ${ringSize}`"
				preserveAspectRatio="xMidYMid meet"
			>
				<defs>
					<linearGradient
						id="progressGradient"
						x1="0%"
						y1="0%"
						x2="100%"
						y2="100%"
					>
						<stop offset="0%" style="stop-color: #007bff; stop-opacity: 1" />
						<stop offset="100%" style="stop-color: #00d4ff; stop-opacity: 1" />
					</linearGradient>
				</defs>
				<!-- Группа для вращения кольца на -90° (начало прогресса с позиции 12 часов) -->
				<g :transform="`rotate(-90 ${ringSize / 2} ${ringSize / 2})`">
					<!-- Фоновое кольцо -->
					<circle
						class="progress-ring__background"
						:stroke-width="strokeWidth"
						:r="normalizedRadius"
						:cx="ringSize / 2"
						:cy="ringSize / 2"
					/>
					<!-- Прогресс кольцо -->
					<circle
						class="progress-ring__circle"
						:stroke-dasharray="`${circumference} ${circumference}`"
						:stroke-dashoffset="strokeDashoffset"
						:stroke-width="strokeWidth"
						:r="normalizedRadius"
						:cx="ringSize / 2"
						:cy="ringSize / 2"
					/>
				</g>
			</svg>

			<!-- Кнопка -->
			<button
				@click="scrollToTop"
				@mouseenter="isHovered = true"
				@mouseleave="isHovered = false"
				class="scroll-to-top-btn"
				:class="{ pulse: shouldPulse }"
				:aria-label="`Прокрутить наверх. ${scrollPercentage}% страницы просмотрено`"
				:title="`Наверх (${scrollPercentage}%)`"
			>
				<!-- Иконка стрелки -->
				<svg
					class="scroll-icon"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="2.5"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M8.25 6.75 12 3m0 0 3.75 3.75M12 3v18"
					/>
				</svg>

				<!-- Процент прокрутки -->
				<span v-if="showPercentage" class="scroll-percentage">
					{{ scrollPercentage }}%
				</span>
			</button>

			<!-- Подсказка при первом появлении -->
			<transition name="tooltip">
				<div v-if="showTooltip" class="tooltip">Вернуться наверх</div>
			</transition>
		</div>
	</transition>
</template>

<script>
export default {
	name: 'ScrollToTop',
	props: {
		// Настраиваемые параметры
		threshold: {
			type: Number,
			default: 300,
		},
		showProgress: {
			type: Boolean,
			default: true,
		},
		showPercentageOnHover: {
			type: Boolean,
			default: true,
		},
		animationDuration: {
			type: Number,
			default: 600,
		},
	},
	data() {
		return {
			isVisible: false,
			isHovered: false,
			scrollPercentage: 0,
			shouldPulse: false,
			showTooltip: false,
			// Размер SVG-контейнера для прогресс-кольца (50px кнопка + 16px для кольца)
			ringSize: 66,
			// Толщина обводки прогресс-кольца (увеличена до 4px для лучшей видимости)
			strokeWidth: 4,
			scrollTimer: null,
			lastScrollTop: 0,
		}
	},
	computed: {
		/**
		 * Нормализованный радиус круга с учетом толщины обводки
		 * Вычитаем двойную толщину обводки из размера контейнера
		 */
		normalizedRadius() {
			return (this.ringSize - this.strokeWidth * 2) / 2
		},
		/**
		 * Длина окружности прогресс-кольца
		 * Используется для расчета stroke-dasharray
		 */
		circumference() {
			return this.normalizedRadius * 2 * Math.PI
		},
		/**
		 * Смещение обводки для отображения прогресса
		 * При 0% - полное смещение (кольцо не видно)
		 * При 100% - нулевое смещение (кольцо полностью заполнено)
		 */
		strokeDashoffset() {
			return (
				this.circumference - (this.scrollPercentage / 100) * this.circumference
			)
		},
		/**
		 * Показывать ли процент прокрутки (только при наведении)
		 */
		showPercentage() {
			return this.showPercentageOnHover && this.isHovered
		},
	},
	mounted() {
		this.throttledHandleScroll = this.throttle(this.handleScroll, 16)
		window.addEventListener('scroll', this.throttledHandleScroll, {
			passive: true,
		})

		// Показать подсказку при первом появлении
		this.checkFirstVisit()

		// Слушаем нажатие клавиш
		window.addEventListener('keydown', this.handleKeydown)
	},
	beforeUnmount() {
		window.removeEventListener('scroll', this.throttledHandleScroll)
		window.removeEventListener('keydown', this.handleKeydown)
		if (this.scrollTimer) clearTimeout(this.scrollTimer)
	},
	methods: {
		throttle(func, delay) {
			let timeoutId
			let lastExecTime = 0
			return function (...args) {
				const currentTime = Date.now()
				if (currentTime - lastExecTime > delay) {
					func.apply(this, args)
					lastExecTime = currentTime
				} else {
					clearTimeout(timeoutId)
					timeoutId = setTimeout(() => {
						func.apply(this, args)
						lastExecTime = Date.now()
					}, delay - (currentTime - lastExecTime))
				}
			}
		},

		/**
		 * Обработчик события прокрутки страницы
		 * Рассчитывает прогресс прокрутки и управляет видимостью кнопки
		 */
		handleScroll() {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop
			const windowHeight = window.innerHeight
			const documentHeight = document.documentElement.scrollHeight

			// Показываем кнопку только после прокрутки на заданное расстояние
			this.isVisible = scrollTop > this.threshold

			// Расчет процента прокрутки по формуле: (scrollTop / (scrollHeight - clientHeight)) * 100
			// Это дает точное значение от 0% (начало страницы) до 100% (конец страницы)
			const scrollPercent = Math.round(
				(scrollTop / (documentHeight - windowHeight)) * 100
			)
			this.scrollPercentage = Math.min(scrollPercent, 100)

			// Сохраняем текущую позицию для отслеживания направления прокрутки
			this.lastScrollTop = scrollTop

			// Активируем пульсацию при достижении конца страницы (95%+)
			if (this.scrollPercentage >= 95 && !this.shouldPulse) {
				this.shouldPulse = true
				setTimeout(() => {
					this.shouldPulse = false
				}, 3000)
			}

			// Скрываем подсказку при начале прокрутки
			if (this.showTooltip) {
				this.showTooltip = false
			}
		},

		scrollToTop() {
			const startPosition = window.pageYOffset
			const startTime = performance.now()

			const easeInOutCubic = t => {
				return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
			}

			const animateScroll = currentTime => {
				const elapsed = currentTime - startTime
				const progress = Math.min(elapsed / this.animationDuration, 1)
				const easeProgress = easeInOutCubic(progress)

				window.scrollTo(0, startPosition * (1 - easeProgress))

				if (progress < 1) {
					requestAnimationFrame(animateScroll)
				} else {
					// Анимация завершена
					this.onScrollComplete()
				}
			}

			requestAnimationFrame(animateScroll)

			// Haptic feedback для мобильных устройств
			if (window.navigator.vibrate) {
				window.navigator.vibrate(10)
			}
		},

		onScrollComplete() {
			// Можно добавить дополнительные действия после завершения прокрутки
			this.$emit('scroll-complete')
		},

		handleKeydown(e) {
			// Ctrl/Cmd + Home для быстрой прокрутки наверх
			if ((e.ctrlKey || e.metaKey) && e.key === 'Home') {
				e.preventDefault()
				this.scrollToTop()
			}
		},

		checkFirstVisit() {
			const hasVisited = localStorage.getItem('scroll-button-visited')
			if (!hasVisited && this.isVisible) {
				setTimeout(() => {
					this.showTooltip = true
					localStorage.setItem('scroll-button-visited', 'true')
          this.pulseTimer = setTimeout(() => {
              this.shouldPulse = false
              this.pulseTimer = null
          }, 3000)
				}, 1000)
			}
		},
	},
}
</script>

<style scoped>
.scroll-to-top-container {
	/* Фиксированное позиционирование - кнопка остается в правом нижнем углу при прокрутке */
	position: fixed;
	bottom: 30px;
	right: 30px;
	z-index: 1000;
	/* Фиксированные размеры контейнера для предотвращения деформации */
	width: 66px;
	height: 66px;
	/* position: fixed создает positioning context для дочерних absolute элементов */
}

/* Прогресс-кольцо */
.progress-ring {
	position: absolute;
	top: 50%;
	left: 50%;
	/* Только позиционирование - вращение выполняется через SVG transform */
	transform: translate(-50%, -50%);
	pointer-events: none;
	/* Обеспечиваем идеально круглую форму */
	display: block;
	/* Предотвращаем сжатие */
	flex-shrink: 0;
}

/* Фоновое кольцо */
.progress-ring__background {
	fill: transparent;
	stroke: rgba(0, 123, 255, 0.15);
	stroke-linecap: round;
}

/* Прогресс кольцо */
.progress-ring__circle {
	fill: transparent;
	stroke: url(#progressGradient);
	stroke-linecap: round;
	transition: stroke-dashoffset 0.3s ease;
	filter: drop-shadow(0 0 6px rgba(0, 123, 255, 0.5));
}

/* Основная кнопка */
.scroll-to-top-btn {
	/* Абсолютное позиционирование для идеального центрирования */
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
  padding: 0;
  margin: 0;
	/* Точные размеры кнопки */
	width: 50px;
	height: 50px;

	/* Гарантируем идеально круглую форму (соотношение сторон 1:1) */
	aspect-ratio: 1 / 1;
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	border: 2px solid rgba(255, 255, 255, 0.15);
	border-radius: 50%;
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 20px rgba(0, 123, 255, 0.35),
		inset 0 1px 0 rgba(255, 255, 255, 0.2);
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
}

/* Эффект волны при клике */
.scroll-to-top-btn::after {
	content: '';
	position: absolute;
	top: 50%;
	left: 50%;
	width: 0;
	height: 0;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.3);
	transform: translate(-50%, -50%);
	transition: width 0.6s, height 0.6s;
}

.scroll-to-top-btn:active::after {
	width: 80px;
	height: 80px;
}

/* Hover эффекты */
.scroll-to-top-btn:hover {
	/* Сохраняем центрирование при hover с небольшим подъемом */
	transform: translate(-50%, calc(-50% - 3px)) scale(1.08);
	box-shadow: 0 8px 30px rgba(0, 123, 255, 0.5),
		inset 0 1px 0 rgba(255, 255, 255, 0.3);
	background: linear-gradient(135deg, #0056b3 0%, #007bff 100%);
	border-color: rgba(255, 255, 255, 0.25);
}

.scroll-to-top-btn:active {
	/* Сохраняем центрирование при нажатии с небольшим подъемом */
	transform: translate(-50%, calc(-50% - 1px)) scale(1.02);
	box-shadow: 0 2px 10px rgba(0, 123, 255, 0.4),
		inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.scroll-to-top-btn:focus-visible {
	outline: none;
	box-shadow: 0 4px 20px rgba(0, 123, 255, 0.35),
		0 0 0 3px rgba(0, 123, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* Иконка стрелки */
.scroll-icon {
	width: 22px;
	height: 22px;
	transition: transform 0.3s ease;
	z-index: 2;
	/* Гарантируем видимость иконки */
	flex-shrink: 0;
}

.scroll-to-top-btn:hover .scroll-icon {
	transform: translateY(-3px);
	animation: bounce 0.6s ease infinite;
}

@keyframes bounce {
	0%,
	100% {
		transform: translateY(-3px);
	}
	50% {
		transform: translateY(-5px);
	}
}

/* Процент прокрутки */
.scroll-percentage {
	position: absolute;
	bottom: -25px;
	left: 50%;
	transform: translateX(-50%);
	font-size: 11px;
	font-weight: 700;
	color: var(--azure-radiance, #007bff);
	background: white;
	padding: 3px 10px;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	white-space: nowrap;
	pointer-events: none;
	animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateX(-50%) translateY(5px);
	}
	to {
		opacity: 1;
		transform: translateX(-50%) translateY(0);
	}
}

/* Подсказка */
.tooltip {
	position: absolute;
	bottom: 60px;
	right: 0;
	background: rgba(0, 0, 0, 0.85);
	color: white;
	padding: 8px 14px;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 500;
	white-space: nowrap;
	pointer-events: none;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.tooltip::after {
	content: '';
	position: absolute;
	bottom: -6px;
	right: 25px;
	width: 12px;
	height: 12px;
	background: rgba(0, 0, 0, 0.85);
	transform: rotate(45deg);
}

/* Анимация пульсации */
@keyframes pulse {
	0%,
	100% {
		box-shadow: 0 4px 20px rgba(0, 123, 255, 0.35),
			0 0 0 0 rgba(0, 123, 255, 0.5);
	}
	50% {
		box-shadow: 0 4px 20px rgba(0, 123, 255, 0.35),
			0 0 0 15px rgba(0, 123, 255, 0);
	}
}

.scroll-to-top-btn.pulse {
	animation: pulse 2s infinite;
}

/* Анимации появления/исчезновения */
.scroll-to-top-enter-active,
.scroll-to-top-leave-active {
	transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.scroll-to-top-enter-from {
	opacity: 0;
	transform: translateY(100px) scale(0.3) rotate(180deg);
}

.scroll-to-top-leave-to {
	opacity: 0;
	transform: translateY(100px) scale(0.3) rotate(-180deg);
}

.tooltip-enter-active,
.tooltip-leave-active {
	transition: all 0.3s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
	opacity: 0;
	transform: translateY(10px);
}

/* Адаптивность */
@media (max-width: 768px) {
	.scroll-to-top-container {
		bottom: 20px;
		right: 20px;
		/* Адаптивный размер контейнера (45px кнопка + 16px для кольца) */
		width: 61px;
		height: 61px;
	}

	.scroll-to-top-btn {
		width: 45px;
		height: 45px;
		min-width: 45px;
		min-height: 45px;
	}

	.scroll-icon {
		width: 20px;
		height: 20px;
	}

	.tooltip {
		font-size: 12px;
		bottom: 55px;
		padding: 6px 12px;
	}

	.scroll-percentage {
		font-size: 10px;
		bottom: -23px;
		padding: 2px 8px;
	}
}

@media (max-width: 480px) {
	.scroll-to-top-container {
		bottom: 16px;
		right: 16px;
		/* Адаптивный размер контейнера (42px кнопка + 16px для кольца) */
		width: 58px;
		height: 58px;
	}

	.scroll-to-top-btn {
		width: 42px;
		height: 42px;
		min-width: 42px;
		min-height: 42px;
	}

	.scroll-icon {
		width: 18px;
		height: 18px;
	}

	.tooltip {
		font-size: 11px;
		bottom: 52px;
		padding: 6px 10px;
	}

	.scroll-percentage {
		font-size: 10px;
		bottom: -22px;
	}
}

/* Темная тема */
@media (prefers-color-scheme: dark) {
	.scroll-to-top-btn {
		background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
		box-shadow: 0 4px 20px rgba(26, 115, 232, 0.4),
			inset 0 1px 0 rgba(255, 255, 255, 0.1);
		border-color: rgba(255, 255, 255, 0.08);
	}

	.scroll-to-top-btn:hover {
		box-shadow: 0 8px 30px rgba(26, 115, 232, 0.6),
			inset 0 1px 0 rgba(255, 255, 255, 0.15);
	}

	.scroll-percentage {
		background: rgba(30, 30, 30, 0.95);
		color: #1a73e8;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	}

	.progress-ring__circle {
		filter: drop-shadow(0 0 8px rgba(26, 115, 232, 0.7));
	}

	.progress-ring__background {
		stroke: rgba(26, 115, 232, 0.2);
	}

	.tooltip {
		background: rgba(20, 20, 20, 0.95);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
	}

	.tooltip::after {
		background: rgba(20, 20, 20, 0.95);
	}
}

/* Улучшенная доступность */
@media (prefers-reduced-motion: reduce) {
	.scroll-to-top-btn,
	.scroll-icon,
	.scroll-to-top-enter-active,
	.scroll-to-top-leave-active,
	.tooltip-enter-active,
	.tooltip-leave-active,
	.progress-ring__circle {
		transition: none !important;
		animation: none !important;
	}

	.scroll-to-top-btn.pulse {
		animation: none !important;
	}

	.scroll-to-top-btn:hover .scroll-icon {
		animation: none !important;
		transform: translateY(-2px);
	}

	.scroll-to-top-btn::after {
		display: none;
	}
}

/* Высокая контрастность */
@media (prefers-contrast: high) {
	.scroll-to-top-btn {
		border: 2px solid white;
		background: #0056b3;
	}

	.scroll-to-top-btn:hover {
		background: #007bff;
		border-color: white;
	}

	.progress-ring__circle {
		stroke: #00d4ff;
		filter: none;
	}

	.progress-ring__background {
		stroke: rgba(255, 255, 255, 0.3);
	}

	.tooltip {
		background: black;
		border: 1px solid white;
	}

	.scroll-percentage {
		border: 1px solid #007bff;
	}
}

/* Дополнительные улучшения для больших экранов */
@media (min-width: 1200px) {
	.scroll-to-top-btn:hover {
		/* Сохраняем центрирование при hover на больших экранах */
		transform: translate(-50%, calc(-50% - 4px)) scale(1.1);
	}

	.scroll-icon {
		width: 22px;
		height: 22px;
	}
}

/* Ориентация landscape для мобильных */
@media (max-width: 768px) and (orientation: landscape) {
	.scroll-to-top-container {
		bottom: 10px;
		right: 10px;
		/* Адаптивный размер контейнера (40px кнопка + 16px для кольца) */
		width: 56px;
		height: 56px;
	}

	.scroll-to-top-btn {
		width: 40px;
		height: 40px;
		min-width: 40px;
		min-height: 40px;
	}

	.scroll-icon {
		width: 16px;
		height: 16px;
	}
}

/* Печать - скрыть кнопку */
@media print {
	.scroll-to-top-container {
		display: none !important;
	}
}

/* Hover эффект для всего контейнера - усиление свечения прогресс-кольца */
.scroll-to-top-container:hover .progress-ring__circle {
	filter: drop-shadow(0 0 10px rgba(0, 123, 255, 0.8));
	stroke-width: 4.5;
	transition: all 0.3s ease;
}

/* Hover эффект для фонового кольца - увеличение прозрачности */
.scroll-to-top-container:hover .progress-ring__background {
	stroke: rgba(0, 123, 255, 0.25);
	transition: stroke 0.3s ease;
}

/* Дополнительная видимость для пользователей с плохим зрением */
@media (prefers-contrast: more) {
	.scroll-to-top-btn {
		border-width: 3px;
	}

	.progress-ring__circle {
		stroke-width: 5;
	}
}

/* Плавная анимация для фонового кольца */
.progress-ring__background {
	transition: stroke 0.3s ease;
}

/* Улучшение читаемости процента */
.scroll-percentage {
	letter-spacing: 0.3px;
}

/* Дополнительная анимация при достижении 100% */
@keyframes completeAnimation {
	0%,
	100% {
		transform: translate(-50%, -50%) rotate(-90deg) scale(1);
	}
	50% {
		transform: translate(-50%, -50%) rotate(-90deg) scale(1.05);
	}
}

.progress-ring.complete {
	animation: completeAnimation 0.5s ease;
}

/* Улучшение для touch устройств */
@media (hover: none) and (pointer: coarse) {
	.scroll-to-top-btn {
		/* Увеличиваем область нажатия для touch */
		padding: 5px;
	}

	.scroll-to-top-btn:active {
		/* Сохраняем центрирование при нажатии на touch устройствах */
		transform: translate(-50%, -50%) scale(0.95);
	}
}

/* Дополнительные стили для Safari */
@supports (-webkit-backdrop-filter: blur(10px)) {
	.scroll-to-top-btn {
		-webkit-backdrop-filter: blur(10px);
		backdrop-filter: blur(10px);
	}
}

/* Оптимизация производительности */
.scroll-to-top-container,
.progress-ring,
.scroll-to-top-btn {
	will-change: transform, opacity;
}

/* Финальные штрихи */
.scroll-to-top-btn:not(:disabled):not(.disabled) {
	cursor: pointer;
}

/* Стиль для disabled состояния (на случай будущего использования) */
.scroll-to-top-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	pointer-events: none;
}
</style>
